import {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    FiBookOpen,
    FiChevronLeft,
    FiChevronRight,
    FiClipboard,
    FiCreditCard,
    FiDatabase,
    FiFileText,
    FiLogOut,
    FiPlusCircle,
    FiSettings,
    FiTable,
    FiUploadCloud,
    FiUser,
    FiUsers,
    FiX
} from "react-icons/fi";
import {NavLink, useNavigate} from "react-router-dom";
import {useExamLock} from "../../../context/ExamLockContext";
import {useAuth} from "../../../context/AuthContext";
import LogoutConfirmModal from "../logoutmodal/LogoutConfirmModal";

import styles from "./Sidebar.module.css";

const Sidebar = ({isMobileOpen = false, onMobileClose, collapsed = false, onToggleCollapse}) => {
    const {t} = useTranslation();
    const {user, logout} = useAuth();
    const navigate = useNavigate();
    // A student sitting an exam must not be able to walk out of it via the menu: leaving the
    // page abandons the attempt, and on a proctored exam the blur/tab-switch watcher would
    // disqualify them outright. The submit button is the only intended way out.
    const {examLocked} = useExamLock();
    const [isLogoutOpen, setIsLogoutOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogoutClick = () => {
        if (isLoggingOut) return;
        setIsLogoutOpen(true);
    };

    const handleLogoutCancel = () => {
        if (isLoggingOut) return;
        setIsLogoutOpen(false);
    };

    const handleLogoutConfirm = async () => {
        if (isLoggingOut) return;
        try {
            setIsLoggingOut(true);
            await logout();
            setIsLogoutOpen(false);
            if (onMobileClose) onMobileClose();
            navigate("/login", {replace: true});
        } finally {
            // Reset in case navigation didn't happen (e.g. logout threw and was swallowed
            // by AuthContext — modal stays open with buttons re-enabled for retry).
            setIsLoggingOut(false);
        }
    };

    const userRoles = (user?.roles || []).map((r) => (r === "OWNER" ? "ADMIN" : r));

    const hasAccess = (itemRoles) => {
        if (!itemRoles || itemRoles.length === 0) return true;
        if (!userRoles.length) return false;
        return userRoles.some((role) => itemRoles.includes(role));
    };

    const sections = [
        {
            title: t("sidebar.subjects"),
            items: [
                {
                    label: t("sidebar.subjectBase"),
                    path: "/subjects/base",
                    icon: <FiBookOpen/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER"],
                },
            ],
        },
        {
            title: t("sidebar.students"),
            items: [
                {
                    label: t("sidebar.studentPanel"),
                    path: "/students",
                    icon: <FiUser/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER"],
                },
            ],
        },
        {
            title: t("sidebar.exams"),
            items: [
                {
                    label: t("sidebar.createExam"),
                    path: "/exams/wizard",
                    icon: <FiPlusCircle/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER"],
                },
                {
                    label: t("sidebar.questionBank"),
                    path: "/question-bank",
                    icon: <FiDatabase/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER"],
                },
                {
                    label: t("sidebar.questionImport", {defaultValue: "Sənəddən idxal"}),
                    path: "/question-bank/import",
                    icon: <FiUploadCloud/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER"],
                },
                {
                    label: t("sidebar.examTemplates"),
                    path: "/exams/base",
                    icon: <FiTable/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER"],
                },
                {
                    label: t("sidebar.examPanel"),
                    path: "/exams/panel",
                    icon: <FiClipboard/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER"],
                },
                {
                    label: t("sidebar.myExams"),
                    path: "/exams/my",
                    icon: <FiClipboard/>,
                    roles: ["STUDENT"],
                },
            ],
        },
        {
            title: t("sidebar.users"),
            items: [
                {
                    label: t("sidebar.userPanel"),
                    path: "/users",
                    icon: <FiUsers/>,
                    roles: ["ADMIN"],
                },
            ],
        },
        {
            title: t("sidebar.settingsSection"),
            items: [
                {
                    label: t("sidebar.settings"),
                    path: "/settings",
                    icon: <FiSettings/>,
                    roles: ["ADMIN", "COORDINATOR", "TEACHER", "STUDENT"],
                },
                {
                    label: t("sidebar.subscription"),
                    path: "/settings/subscription",
                    icon: <FiCreditCard/>,
                    roles: ["ADMIN"],
                },
                {
                    label: t("sidebar.invoices"),
                    path: "/settings/invoices",
                    icon: <FiFileText/>,
                    roles: ["ADMIN"],
                },
            ],
        },
    ];

    const visibleSections = sections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => hasAccess(item.roles)),
        }))
        .filter((section) => section.items.length > 0);

    return (
        <>
            {isMobileOpen && <div className={styles.overlay} onClick={onMobileClose} aria-hidden="true"/>}

            <aside className={`${styles.sidebar} ${isMobileOpen ? styles.sidebarOpen : ""} ${collapsed ? styles.collapsed : ""}`}>
                <div className={styles.brand}>
                    <img className={styles.brandLogoImg} src="/gredus-logo.svg" alt="Gredus Logo"/>

                    <div className={styles.userInfo}>
                        <span className={styles.userName}>{user?.name || user?.username || t("common.user")}</span>
                        {user?.tenantName && (
                            <span className={styles.tenantName} title={user.tenantName}>{user.tenantName}</span>
                        )}
                    </div>

                    {/* Desktop collapse/expand toggle (hidden on mobile, where the drawer is used). */}
                    <button
                        type="button"
                        className={styles.collapseToggle}
                        onClick={onToggleCollapse}
                        title={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
                        aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
                    >
                        {collapsed ? <FiChevronRight size={18}/> : <FiChevronLeft size={18}/>}
                    </button>

                    <button type="button" className={styles.closeBtn} onClick={onMobileClose}>
                        <FiX size={18}/>
                    </button>
                </div>

                <nav className={styles.nav}>
                    {visibleSections.map((section) => (
                        <div className={styles.section} key={section.title}>
                            <div className={styles.sectionTitle}>{section.title}</div>

                            {section.items.map((item) => (
                                examLocked ? (
                                    // Rendered as a plain span, not a disabled NavLink: an <a href>
                                    // stays keyboard-focusable and middle-clickable into a new tab,
                                    // which on a proctored exam is precisely what triggers the
                                    // tab-switch disqualification.
                                    <span key={item.path}
                                          className={`${styles.link} ${styles.linkDisabled}`}
                                          aria-disabled="true"
                                          title={t("pages.examSession.navLockedHint", {defaultValue: "İmtahan bitənə qədər əlçatmazdır"})}>
                                        <span className={styles.linkIcon}>{item.icon}</span>
                                        <span className={styles.linkLabel}>{item.label}</span>
                                    </span>
                                ) : (
                                    <NavLink key={item.path} to={item.path}
                                             end
                                             title={item.label}
                                             className={({isActive}) => (isActive ? `${styles.link} ${styles.activeLink}` : styles.link)}
                                             onClick={onMobileClose}>
                                        <span className={styles.linkIcon}>{item.icon}</span>
                                        <span className={styles.linkLabel}>{item.label}</span>
                                    </NavLink>
                                )
                            ))}
                        </div>
                    ))}

                    <div className={styles.logoutSection}>
                        {/* Logging out mid-exam abandons the attempt with no way back in. */}
                        <button className={styles.logoutBtn} type="button" onClick={handleLogoutClick}
                                disabled={isLoggingOut || examLocked}
                                title={examLocked
                                    ? t("pages.examSession.navLockedHint", {defaultValue: "İmtahan bitənə qədər əlçatmazdır"})
                                    : t("common.logout")}>
                            <FiLogOut size={18}/>
                            <span className={styles.logoutLabel}>{t("common.logout")}</span>
                        </button>
                    </div>

                    <div className={styles.legalSection}>
                        {/* target="_blank": on a proctored exam the resulting focus loss is itself a
                            disqualification trigger, so this one must go too. */}
                        {examLocked ? (
                            <span className={`${styles.legalLink} ${styles.linkDisabled}`}
                                  aria-disabled="true"
                                  title={t("pages.examSession.navLockedHint", {defaultValue: "İmtahan bitənə qədər əlçatmazdır"})}>
                                <FiFileText size={16}/>
                                <span className={styles.legalLabel}>{t("legal.agreementLinkLabel")}</span>
                            </span>
                        ) : (
                            <a className={styles.legalLink}
                               href="/terms"
                               target="_blank"
                               rel="noopener noreferrer"
                               title={t("legal.agreementLinkLabel")}>
                                <FiFileText size={16}/>
                                <span className={styles.legalLabel}>{t("legal.agreementLinkLabel")}</span>
                            </a>
                        )}
                    </div>
                </nav>

                <LogoutConfirmModal isOpen={isLogoutOpen}
                                    onConfirm={handleLogoutConfirm}
                                    onCancel={handleLogoutCancel}
                                    loading={isLoggingOut}/>
            </aside>
        </>
    );
};

export default Sidebar;
